import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { IoClose } from "react-icons/io5";
import { TfiImport } from "react-icons/tfi";
import DesktopBudgetHeader from "../components/DesktopBudgetHeader";
import { AppLoader, ButtonLoader } from "../components/Loaders";
import { APP_NAME } from "../constants/app";
import { BUDGET_MODULE_PATHS } from "../constants/budgetModule";
import {
  getSelectedBudgetModuleYear,
  resetBudgetFlow,
  saveSelectedBudgetModuleYear,
  setBudgetFlowStarted,
} from "../utils/budgetFlow";
import {
  buildImportedBudgetRows,
  normalizeWorksheetRows,
} from "../utils/budgetWorkspace";
import { getWorkspace, saveWorkspace } from "../api/budgetPortal";
import { useAuth } from "../state/AuthContext";

const BudgetImportHeaders = {
  lineItem: "Line Item",
  actualPriorYear: "Actual Prior Year (optional)",
};

const emptySelectionFormat = {
  fyYear: "",
  lineItem: "",
  actualPriorYear: "",
};

const getFinancialYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const options = [];

  for (let year = 2006; year < currentYear; year += 1) {
    options.push(`${year}-${year + 1}`);
  }

  return options;
};

const ImportBudgetPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [rawRows, setRawRows] = useState([]);
  const [headerOptions, setHeaderOptions] = useState([]);
  const [fileName, setFileName] = useState("");
  const [selectedYear, setSelectedYear] = useState(
    getSelectedBudgetModuleYear(),
  );
  const [sheetChoices, setSheetChoices] = useState([]);
  const [pendingWorkbook, setPendingWorkbook] = useState(null);
  const [isSheetModalOpen, setSheetModalOpen] = useState(false);
  const [isMappingModalOpen, setMappingModalOpen] = useState(false);
  const [selectionFormat, setSelectionFormat] = useState(emptySelectionFormat);

  const fyOptions = useMemo(() => getFinancialYearOptions(), []);

  useEffect(() => {
    const bootstrapSelectedYear = getSelectedBudgetModuleYear();
    if (bootstrapSelectedYear) {
      setSelectedYear(bootstrapSelectedYear);
      setSelectionFormat((currentValue) => ({
        ...currentValue,
        fyYear: bootstrapSelectedYear,
      }));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!selectedYear) {
      return;
    }

    if (location.state?.skipDraftRedirect) {
      return;
    }

    const checkExistingDraft = async () => {
      try {
        const response = await getWorkspace(token, selectedYear);
        const importedRows =
          response?.data?.workspace?.bootstrap_metadata?.import_session
            ?.imported_rows ?? [];

        if (importedRows.length > 0) {
          navigate(BUDGET_MODULE_PATHS.mapping, { replace: true });
        }
      } catch (_error) {
        // Leave the user on import when the workspace lookup fails.
      }
    };

    checkExistingDraft();
  }, [location.state, navigate, selectedYear, token]);

  useEffect(() => {
    setSelectionFormat((currentValue) => ({
      ...currentValue,
      fyYear: selectedYear,
    }));
  }, [selectedYear]);

  const resetImportState = () => {
    setError("");
    setRawRows([]);
    setHeaderOptions([]);
    setFileName("");
    setSheetChoices([]);
    setPendingWorkbook(null);
    setSheetModalOpen(false);
    setMappingModalOpen(false);
    setSelectionFormat((currentValue) => ({
      ...emptySelectionFormat,
      fyYear: currentValue.fyYear || selectedYear,
    }));
  };

  const processWorkbookSheet = (workbook, sheetName, importedFileName) => {
    const sheet = workbook.Sheets[sheetName];
    const sheetRows = XLSX.utils.sheet_to_json(sheet, {
      header: 1,
      defval: "",
    });
    const normalizedWorksheet = normalizeWorksheetRows(sheetRows);

    if (
      !normalizedWorksheet.headers.length ||
      !normalizedWorksheet.rows.length
    ) {
      setError("No usable data is present in the selected budget worksheet.");
      return;
    }

    setFileName(importedFileName);
    setRawRows(normalizedWorksheet.rows);
    setHeaderOptions(normalizedWorksheet.headers);
    setMappingModalOpen(true);
    setSelectionFormat((currentValue) => ({
      ...emptySelectionFormat,
      fyYear: currentValue.fyYear || selectedYear,
    }));
  };

  const loadWorkbookFromFile = async (uploadedFile) => {
    if (!uploadedFile) {
      setError("Please select a file.");
      return;
    }

    try {
      const arrayBuffer = await uploadedFile.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: "array" });

      if (workbook.SheetNames.length === 1) {
        processWorkbookSheet(
          workbook,
          workbook.SheetNames[0],
          uploadedFile.name,
        );
      } else {
        setPendingWorkbook({ workbook, fileName: uploadedFile.name });
        setSheetChoices(workbook.SheetNames);
        setSheetModalOpen(true);
      }
    } catch (uploadError) {
      setError(uploadError.message || "Invalid file");
    }
  };

  const handleFileUpload = async (event) => {
    const uploadedFile = event.target.files?.[0];
    if (!uploadedFile) {
      setError("Please select a file.");
      return;
    }

    setError("");
    event.target.value = "";
    await loadWorkbookFromFile(uploadedFile);
  };

  const handleApplyMapping = async () => {
    if (!selectionFormat.fyYear) {
      setError("Please select the financial year to be imported.");
      return;
    }

    if (!selectionFormat.lineItem) {
      setError("Please map the line item column before continuing.");
      return;
    }

    const importedRows = buildImportedBudgetRows({
      rows: rawRows,
      mapping: selectionFormat,
    });

    if (!importedRows.length) {
      setError("No usable budget rows were found after applying the mapping.");
      return;
    }

    setSaving(true);
    setError("");

    try {
      const existingWorkspaceResponse = await getWorkspace(
        token,
        selectionFormat.fyYear,
      );
      const existingWorkspace =
        existingWorkspaceResponse?.data?.workspace ?? {};
      const preservedBudgetRows = existingWorkspace.budget_rows ?? null;

      await saveWorkspace(token, selectionFormat.fyYear, {
        budget_rows: preservedBudgetRows,
        planner_details: existingWorkspace.planner_details ?? null,
        department_details: existingWorkspace.department_details ?? null,
        bootstrap_metadata: {
          ...(existingWorkspace.bootstrap_metadata ?? {}),
          import_session: {
            file_name: fileName,
            imported_at: new Date().toISOString(),
            mapping: selectionFormat,
            imported_rows: importedRows,
            requires_prior_estimate_import: false,
            requires_prior_actual_import: false,
            amounts_initialized_to_zero: true,
            status: "imported",
            preserved_budget_rows: preservedBudgetRows,
          },
        },
      });

      saveSelectedBudgetModuleYear(selectionFormat.fyYear);
      setBudgetFlowStarted();
      setMappingModalOpen(false);
      navigate(BUDGET_MODULE_PATHS.mapping);
    } catch (saveError) {
      setError(
        saveError?.response?.data?.message ||
          saveError.message ||
          "Unable to save the import draft.",
      );
    }

    setSaving(false);
  };

  const handleDiscardImport = async () => {
    resetImportState();

    if (!selectedYear) {
      resetBudgetFlow();
      navigate(BUDGET_MODULE_PATHS.entry, { replace: true });
      return;
    }

    setSaving(true);
    setError("");

    try {
      const existingWorkspaceResponse = await getWorkspace(token, selectedYear);
      const existingWorkspace =
        existingWorkspaceResponse?.data?.workspace ?? null;

      if (existingWorkspace?.bootstrap_metadata) {
        const nextBootstrapMetadata = {
          ...existingWorkspace.bootstrap_metadata,
        };
        const preservedBudgetRows =
          nextBootstrapMetadata.import_session?.preserved_budget_rows ?? null;
        delete nextBootstrapMetadata.import_session;

        await saveWorkspace(token, selectedYear, {
          budget_rows: existingWorkspace.budget_rows ?? preservedBudgetRows,
          planner_details: existingWorkspace.planner_details ?? null,
          department_details: existingWorkspace.department_details ?? null,
          bootstrap_metadata: Object.keys(nextBootstrapMetadata).length
            ? nextBootstrapMetadata
            : null,
        });
      }

      resetBudgetFlow();
      navigate(BUDGET_MODULE_PATHS.entry, { replace: true });
    } catch (discardError) {
      setError(
        discardError?.response?.data?.message ||
          discardError.message ||
          "Unable to discard the current import draft.",
      );
    }

    setSaving(false);
  };

  return (
    <section className="dashboard-layout">
      <DesktopBudgetHeader showSidebar={false} />
      <div className="dashboard-previousBtn">
        <button
          type="button"
          className="landingPage-discardBtn"
          onClick={handleDiscardImport}
          disabled={saving}
        >
          {saving ? <ButtonLoader label="Discarding..." /> : "Discard"}
        </button>
      </div>

      <div className="dashboard-container">
        <div className="dashboard-option-layout">
          <div
            className="dashboard-option-container"
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileUpload}
              style={{ display: "none" }}
              ref={fileInputRef}
            />
            <TfiImport color="#4482F6" size={50} />
            <p className="dashboard-option-title">
              <span className="dashboard-option-title-primary">
                Import Budget
              </span>
              <br />
              (Map budget sheet columns)
            </p>
            <p className="dashboard-option-copy">
              Select a workbook to continue
            </p>
          </div>
        </div>
      </div>

      {error ? <div className="web-inlineError">{error}</div> : null}
      {loading ? <AppLoader label="Loading import workspace..." /> : null}

      {isSheetModalOpen ? (
        <div className="web-modalOverlay">
          <div className="web-modalCard">
            <div className="dashboard-modal-title-container">
              <p className="dashboard-modal-title">
                Select correct budget worksheet
              </p>
              <IoClose
                color="#000"
                size={30}
                onClick={() => setSheetModalOpen(false)}
              />
            </div>
            <div className="dashboard-modal">
              <label className="dashboard-modal-label">
                Worksheet:
                <select
                  className="web-modalSelect"
                  defaultValue=""
                  onChange={(event) => {
                    const selectedSheet = event.target.value;
                    if (!selectedSheet || !pendingWorkbook) {
                      return;
                    }
                    processWorkbookSheet(
                      pendingWorkbook.workbook,
                      selectedSheet,
                      pendingWorkbook.fileName,
                    );
                    setSheetModalOpen(false);
                    setPendingWorkbook(null);
                  }}
                >
                  <option value="">Select a worksheet</option>
                  {sheetChoices.map((sheetName) => (
                    <option key={sheetName} value={sheetName}>
                      {sheetName}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>
        </div>
      ) : null}

      {isMappingModalOpen ? (
        <div className="web-modalOverlay">
          <div className="web-modalCard">
            <div className="dashboard-modal-title-container">
              <p className="dashboard-modal-title">
                {APP_NAME} Budget Header Mapping
              </p>
              <IoClose
                color="#000"
                size={30}
                onClick={() => setMappingModalOpen(false)}
              />
            </div>

            <div className="dashboard-modal">
              <div className="dashboard-modal-optionRow">
                <label className="dashboard-modal-label">
                  Financial Year to be imported:
                  <select
                    className="web-modalSelect"
                    value={selectionFormat.fyYear}
                    onChange={(event) => {
                      setSelectedYear(event.target.value);
                    }}
                  >
                    <option value="">Select a financial year</option>
                    {fyOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="dashboard-modal-optionRow">
                <label className="dashboard-modal-label">
                  {BudgetImportHeaders.lineItem}:
                  <select
                    className="web-modalSelect"
                    value={selectionFormat.lineItem}
                    onChange={(event) =>
                      setSelectionFormat((currentValue) => ({
                        ...currentValue,
                        lineItem: event.target.value,
                      }))
                    }
                  >
                    <option value="">Select a column</option>
                    {headerOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="dashboard-modal-optionRow">
                <label className="dashboard-modal-label">
                  {BudgetImportHeaders.actualPriorYear}:
                  <select
                    className="web-modalSelect"
                    value={selectionFormat.actualPriorYear}
                    onChange={(event) =>
                      setSelectionFormat((currentValue) => ({
                        ...currentValue,
                        actualPriorYear: event.target.value,
                      }))
                    }
                  >
                    <option value="">Select a column</option>
                    {headerOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="dashboard-btn-container">
                <div
                  className="dashboard-cancel-btn"
                  onClick={() => {
                    setMappingModalOpen(false);
                    setRawRows([]);
                    setHeaderOptions([]);
                    setFileName("");
                  }}
                >
                  Cancel
                </div>
                <div
                  className="dashboard-applyMapping-btn"
                  onClick={handleApplyMapping}
                >
                  {saving ? (
                    <ButtonLoader label="Saving..." />
                  ) : (
                    "Apply Mapping"
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default ImportBudgetPage;
